import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IconButton,
  ITextField,
  List,
  PrimaryButton,
  Separator,
  Spinner,
  SpinnerSize,
  Stack,
  StackItem,
  Text,
  TextField,
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import Description from "@mui/icons-material/Description";
import Download from "@mui/icons-material/Download";
import EditOutlined from "@mui/icons-material/EditOutlined";
import Slideshow from "@mui/icons-material/Slideshow";
import TableChart from "@mui/icons-material/TableChart";

import { historyRename, historyDelete } from "../../api";
import { Conversation } from "../../api/models";
import { parseBackendDate } from "../../utils/helpers";
import _ from "lodash";
import { GroupedChatHistory } from "./ChatHistoryList";

import styles from "./ChatHistory.module.css";

interface ChatHistoryListItemCellProps {
  item?: Conversation;
  onSelect: (item: Conversation | null) => void;
  selectedConvId: string;
  onHistoryTitleChange: (id: string, newTitle: string) => void;
  onHistoryDelete: (id: string) => void;
  onExport: (id: string, format: string) => Promise<void>;
  isGenerating: boolean;
  toggleToggleSpinner: (toggler: boolean) => void;
}

interface ChatHistoryListItemGroupsProps {
  fetchingChatHistory: boolean;
  handleFetchHistory?: () => Promise<void>;
  groupedChatHistory: GroupedChatHistory[];
  isSearchActive?: boolean;
  onSelectConversation: (id: string) => void;
  selectedConvId: string;
  onHistoryTitleChange: (id: string, newTitle: string) => void;
  onHistoryDelete: (id: string) => void;
  onExport: (id: string, format: string) => Promise<void>;
  isGenerating: boolean;
  toggleToggleSpinner: (toggler: boolean) => void;
}

export const ChatHistoryListItemCell: React.FC<
  ChatHistoryListItemCellProps
> = ({
  item,
  onSelect,
  selectedConvId,
  onHistoryTitleChange,
  onHistoryDelete,
  onExport,
  isGenerating,
  toggleToggleSpinner,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [edit, setEdit] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [
    hideDeleteDialog,
    { toggle: toggleDeleteDialog, setTrue: closeDeleteDialog },
  ] = useBoolean(true);
  const [errorDelete, setErrorDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [renameLoading, setRenameLoading] = useState(false);
  const [errorRename, setErrorRename] = useState<string | undefined>(undefined);
  const [textFieldFocused, setTextFieldFocused] = useState(false);
  const textFieldRef = useRef<ITextField | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (textFieldFocused && textFieldRef.current) {
      textFieldRef.current.focus();
      setTextFieldFocused(false);
    }
  }, [textFieldFocused]);

  const isSelected = item?.id === selectedConvId;
  const dialogContentProps = {
    type: DialogType.close,
    title: "Are you sure you want to delete this item?",
    closeButtonAriaLabel: "Close",
    subText: "The history of this chat session will be permanently removed.",
  };

  const modalProps = {
    titleAriaId: "labelId",
    subtitleAriaId: "subTextId",
    isBlocking: true,
    styles: { main: { maxWidth: 450 } },
  };

  if (!item) {
    return null;
  }

  const formatDateDisplay = (dateString: string) => {
    const date = parseBackendDate(dateString);

    if (!date) {
      console.warn("Invalid date string:", dateString);
      return "Invalid Date";
    }

    const now = new Date();
    const dateLocal = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const todayLocal = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const yesterdayLocal = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 1,
    );

    const isToday = dateLocal.getTime() === todayLocal.getTime();
    const isYesterday = dateLocal.getTime() === yesterdayLocal.getTime();

    if (isToday || isYesterday) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    toggleToggleSpinner(true);
    try {
      const response = await historyDelete(item.id);
      closeDeleteDialog();
      if (!response.ok) {
        setErrorDelete(true);
        setTimeout(() => {
          setErrorDelete(false);
        }, 5000);
      } else {
        onHistoryDelete(item.id);
      }
    } finally {
      setDeleting(false);
      toggleToggleSpinner(false);
    }
  };

  const onEdit = () => {
    setEdit(true);
    setTextFieldFocused(true);
    setEditTitle(item?.title);
  };

  const handleSelectItem = () => {
    onSelect(item);
  };

  const truncatedTitle =
    item?.title?.length > 28
      ? `${item.title.substring(0, 28)} ...`
      : item.title;

  const handleSaveEdit = async (e: any) => {
    e.preventDefault();
    if (errorRename || renameLoading) {
      return;
    }

    if (_.trim(editTitle) === _.trim(item?.title)) {
      setEdit(false);
      setTextFieldFocused(false);
      return;
    }
    setRenameLoading(true);
    const response = await historyRename(item.id, editTitle);
    if (!response.ok) {
      setErrorRename("Error: could not rename item");
      setTimeout(() => {
        setTextFieldFocused(true);
        setErrorRename(undefined);
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }, 5000);
    } else {
      setRenameLoading(false);
      setEdit(false);
      onHistoryTitleChange(item.id, editTitle);
      setEditTitle("");
    }
  };

  const chatHistoryTitleOnChange = (e: any) => {
    setEditTitle(e.target.value);
  };

  const cancelEditTitle = () => {
    setEdit(false);
    setEditTitle("");
  };

  const handleKeyPressEdit = (e: any) => {
    if (e.key === "Enter") {
      return handleSaveEdit(e);
    }
    if (e.key === "Escape") {
      cancelEditTitle();
      return;
    }
  };
  const onClickDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    toggleDeleteDialog();
  };

  const handleExportClick = async (format: string) => {
    if (item && !isExporting) {
      setIsExporting(true);
      try {
        await onExport(item.id, format);
      } finally {
        setIsExporting(false);
      }
    }
  };

  const exportMenuProps = {
    items: [
      {
        key: "docx",
        text: "Word (.docx)",
        iconProps: {},
        onRenderIcon: () => <Description fontSize="small" />,
        onClick: () => {
          handleExportClick("docx");
        },
      },
      {
        key: "pptx",
        text: "PowerPoint (.pptx)",
        iconProps: {},
        onRenderIcon: () => <Slideshow fontSize="small" />,
        onClick: () => {
          handleExportClick("pptx");
        },
      },
      {
        key: "xlsx",
        text: "Excel (.xlsx)",
        iconProps: {},
        onRenderIcon: () => <TableChart fontSize="small" />,
        onClick: () => {
          handleExportClick("xlsx");
        },
      },
    ],
  };

  const isButtonDisabled = isGenerating && isSelected;
  return (
    <Stack
      key={item.id}
      tabIndex={0}
      aria-label="chat history item"
      className={styles.itemCell}
      onClick={() => handleSelectItem()}
      onKeyDown={(e) =>
        e.key === "Enter" || e.key === " " ? handleSelectItem() : null
      }
      verticalAlign="center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      styles={{
        root: {
          backgroundColor: isSelected ? "#e6e6e6" : "transparent",
        },
      }}
    >
      {edit ? (
        <>
          <Stack.Item style={{ width: "100%" }}>
            <form
              aria-label="edit title form"
              onSubmit={(e) => handleSaveEdit(e)}
              style={{ padding: "5px 0px" }}
            >
              <Stack horizontal verticalAlign={"start"}>
                <Stack.Item style={{ flexGrow: 1 }}>
                  <TextField
                    componentRef={textFieldRef}
                    autoFocus={textFieldFocused}
                    value={editTitle}
                    placeholder={item.title}
                    onChange={chatHistoryTitleOnChange}
                    onKeyDown={handleKeyPressEdit}
                    errorMessage={errorRename}
                    disabled={errorRename ? true : false}
                  />
                </Stack.Item>
                {editTitle && (
                  <Stack.Item>
                    <Stack
                      aria-label="action button group"
                      horizontal
                      verticalAlign={"center"}
                    >
                      <IconButton
                        role="button"
                        disabled={errorRename !== undefined}
                        onKeyDown={(e) =>
                          e.key === " " || e.key === "Enter"
                            ? handleSaveEdit(e)
                            : null
                        }
                        onClick={(e) => handleSaveEdit(e)}
                        aria-label="confirm new title"
                        onRenderIcon={() => <Check fontSize="small" />}
                        styles={{ root: { color: "green", marginLeft: "5px" } }}
                      />
                      <IconButton
                        role="button"
                        disabled={errorRename !== undefined}
                        onKeyDown={(e) =>
                          e.key === " " || e.key === "Enter"
                            ? cancelEditTitle()
                            : null
                        }
                        onClick={() => cancelEditTitle()}
                        aria-label="cancel edit title"
                        onRenderIcon={() => <Close fontSize="small" />}
                        styles={{ root: { color: "red", marginLeft: "5px" } }}
                      />
                    </Stack>
                  </Stack.Item>
                )}
              </Stack>
              {errorRename && (
                <Text
                  role="alert"
                  aria-label={errorRename}
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: "rgb(164,38,44)",
                  }}
                >
                  {errorRename}
                </Text>
              )}
            </form>
          </Stack.Item>
        </>
      ) : (
        <>
          <Stack horizontal verticalAlign={"center"} style={{ width: "100%" }}>
            <div className={styles.chatTitle}>{truncatedTitle}</div>
            {!(isSelected || isHovered) && (
              <Text
                style={{
                  fontSize: 11,
                  color: "#666",
                  marginLeft: "auto",
                  fontStyle: "italic",
                }}
              >
                {formatDateDisplay(item.updatedAt ?? item.date)}
              </Text>
            )}
            {(isSelected || isHovered) && (
              <Stack horizontal horizontalAlign="end">
                <IconButton
                  className={styles.itemButton}
                  disabled={isButtonDisabled}
                  onRenderIcon={() => <DeleteOutlined fontSize="small" />}
                  title="Delete"
                  onClick={onClickDelete}
                  onKeyDown={(e) =>
                    e.key === " " ? toggleDeleteDialog() : null
                  }
                />
                <IconButton
                  className={styles.itemButton}
                  disabled={isButtonDisabled}
                  onRenderIcon={() => <EditOutlined fontSize="small" />}
                  title="Edit"
                  onClick={onEdit}
                  onKeyDown={(e) => (e.key === " " ? onEdit() : null)}
                />
                {isExporting ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                    }}
                  >
                    <Spinner size={SpinnerSize.xSmall} />
                  </div>
                ) : (
                  <IconButton
                    className={styles.itemButton}
                    disabled={isButtonDisabled}
                    onRenderIcon={() => <Download fontSize="small" />}
                    title="Export"
                    menuProps={exportMenuProps}
                    allowDisabledFocus
                    styles={{
                      menuIcon: { display: "none" },
                    }}
                    // --------------------
                  />
                )}
              </Stack>
            )}
          </Stack>
        </>
      )}
      {errorDelete && (
        <Text
          styles={{
            root: { color: "red", marginTop: 5, fontSize: 14 },
          }}
        >
          Error: could not delete item
        </Text>
      )}
      <Dialog
        hidden={hideDeleteDialog}
        onDismiss={deleting ? () => {} : toggleDeleteDialog}
        dialogContentProps={dialogContentProps}
        modalProps={modalProps}
      >
        <DialogFooter>
          {deleting && (
            <Spinner
              size={SpinnerSize.small}
              label="Deleting..."
              labelPosition="right"
              styles={{ root: { display: "inline-flex", marginRight: 12 } }}
            />
          )}
          <PrimaryButton onClick={onDelete} disabled={deleting} text="Delete" />
          <DefaultButton
            onClick={toggleDeleteDialog}
            disabled={deleting}
            text="Cancel"
          />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};

export const ChatHistoryListItemGroups: React.FC<
  ChatHistoryListItemGroupsProps
> = ({
  groupedChatHistory,
  handleFetchHistory,
  fetchingChatHistory,
  isSearchActive = false,
  onSelectConversation,
  selectedConvId,
  onHistoryTitleChange,
  onHistoryDelete,
  onExport,
  isGenerating,
  toggleToggleSpinner,
}) => {
  const observerTarget = useRef(null);
  const handleSelectHistory = (item?: Conversation) => {
    if (typeof item === "object") {
      onSelectConversation(item?.id);
    }
  };

  const onRenderCell = (item?: Conversation) => {
    return (
      <ChatHistoryListItemCell
        item={item}
        onSelect={() => handleSelectHistory(item)}
        selectedConvId={selectedConvId}
        key={item?.id}
        onHistoryTitleChange={onHistoryTitleChange}
        onHistoryDelete={onHistoryDelete}
        onExport={onExport}
        isGenerating={isGenerating}
        toggleToggleSpinner={toggleToggleSpinner}
      />
    );
  };

  useEffect(() => {
    if (!handleFetchHistory || isSearchActive) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleFetchHistory();
        }
      },
      { threshold: 1 },
    );

    if (observerTarget.current) observer.observe(observerTarget.current);

    return () => {
      if (observerTarget.current) observer.unobserve(observerTarget.current);
    };
  }, [handleFetchHistory, isSearchActive, observerTarget.current]);

  const allConversationsLength = groupedChatHistory.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.entries.length,
    0,
  );

  const showSearchLoadingOverlay =
    isSearchActive && fetchingChatHistory && allConversationsLength > 0;

  if (allConversationsLength === 0) {
    if (fetchingChatHistory) {
      return (
        <Stack
          horizontal
          horizontalAlign="center"
          verticalAlign="center"
          style={{ width: "100%", marginTop: 10 }}
        >
          <Spinner
            size={SpinnerSize.small}
            aria-label={
              isSearchActive
                ? "Searching chat history"
                : "loading more chat history"
            }
          />
        </Stack>
      );
    }

    return (
      <Stack
        horizontal
        horizontalAlign="center"
        verticalAlign="center"
        style={{ width: "100%", marginTop: 10 }}
      >
        <StackItem>
          <Text
            style={{ alignSelf: "center", fontWeight: "400", fontSize: 14 }}
          >
            <span>
              {isSearchActive
                ? "No matching conversations."
                : "No chat history."}
            </span>
          </Text>
        </StackItem>
      </Stack>
    );
  }

  return (
    <div
      id="historyListContainer"
      className={styles.listContainer}
      data-is-scrollable
    >
      <div
        className={
          showSearchLoadingOverlay ? styles.listContentDimmed : undefined
        }
      >
        {groupedChatHistory.map(
          (group, index) =>
            group.entries.length > 0 && (
              <Stack
                horizontalAlign="start"
                verticalAlign="center"
                key={`GROUP-${group.title}-${index}`}
                className={styles.chatGroup}
                aria-label={`chat history group: ${group.title}`}
              >
                <Stack aria-label={group.title} className={styles.chatMonth}>
                  {group.title}
                </Stack>
                <List
                  aria-label={`chat history list`}
                  items={group.entries}
                  getKey={(conversation) => conversation.id}
                  onRenderCell={onRenderCell}
                  className={styles.chatList}
                />
              </Stack>
            ),
        )}
        <div id="chatHistoryListItemObserver" ref={observerTarget} />
        <Separator
          styles={{
            root: {
              width: "100%",
              padding: "0px",
              height: "2px",
              position: "relative",
              "::before": {
                backgroundColor: "#d6d6d6",
              },
            },
          }}
        />
      </div>
      {showSearchLoadingOverlay && (
        <div className={styles.searchLoadingOverlay}>
          <Spinner
            size={SpinnerSize.small}
            aria-label="Searching chat history"
          />
        </div>
      )}
      {Boolean(fetchingChatHistory) && !isSearchActive && (
        <div className={styles.spinnerContainer}>
          <Spinner
            size={SpinnerSize.small}
            aria-label="loading more chat history"
            className={styles.spinner}
          />
        </div>
      )}
    </div>
  );
};
